// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import { useAccount, useSendTransaction, useWaitForTransactionReceipt, useReadContracts } from 'wagmi'; // Import useReadContracts
import TopBar from '../components/TopBar.jsx';
import Input from '../components/Input.tsx';
import { web3Clients, volumeBotFeeAddress, apiUrl, coinNames } from '../utils/constants.ts';
import { toast } from 'react-hot-toast';
import ClipLoader from 'react-spinners/ClipLoader';
import { parseEther, isAddress, erc20Abi } from 'viem'; // Import isAddress and erc20Abi

// --- Helper Function ---
function formatStatus(status, tokenSymbol = '') {
    switch (status) {
        case 'pending_deposit': return 'Pending Deposit';
        case 'pending_distribution': return 'Distributing Funds...';
        case 'active': return `Active (${tokenSymbol})`;
        case 'stopping': return `Stopping (${tokenSymbol})...`;
        case 'stopped': return 'Stopped';
        case 'error': return 'Error';
        case 'not_found': return 'Not Configured';
        default: return status || 'Loading...';
    }
}

const VolumeBot = () => {
    const { address, chainId, isConnected } = useAccount(); // Add isConnected
    const currentChainId = chainId || 97; // Default to 97 if not connected

    // --- State ---
    // View Mode
    const [viewMode, setViewMode] = useState('loading'); // 'loading', 'form', 'dashboard', 'error'

    // Form State
    const [tokenContract, setTokenContract] = useState('');
    const [fetchedTokenName, setFetchedTokenName] = useState(''); // State for fetched name
    const [fetchedTokenSymbol, setFetchedTokenSymbol] = useState(''); // State for fetched symbol
    const [buyAmountMin, setBuyAmountMin] = useState('0.012');
    const [buyAmountMax, setBuyAmountMax] = useState('0.022');
    const [walletCount, setWalletCount] = useState(4);
    const [bnbDeposit, setBnbDeposit] = useState('');
    const [buyInterval, setBuyInterval] = useState(60);
    const [sellTriggerPercent, setSellTriggerPercent] = useState(30);
    const [sellPortion, setSellPortion] = useState(50);
    const [stopLossPercent, setStopLossPercent] = useState(10);
    const [calculatedFee, setCalculatedFee] = useState(0);
    const [amountPerWallet, setAmountPerWallet] = useState(0);

    // Dashboard State
    const [botDetails, setBotDetails] = useState(null); // Stores details of the existing bot
    const [displayStatus, setDisplayStatus] = useState('Loading...'); // User-friendly status

    // Submission/Transaction State
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionStatus, setSubmissionStatus] = useState(''); // e.g., 'Configuring...', 'Waiting for Deposit Tx...', 'Distributing...'
    const { data: txHash, sendTransaction } = useSendTransaction(); // Assuming txHash is the correct name for hash
    const [hash, setHash] = useState(txHash); // Local state to manage hash display/clearing
    const { isLoading: isConfirming, isSuccess: isConfirmed } = useWaitForTransactionReceipt({ hash });

    // Update local hash state when txHash changes
    useEffect(() => {
        setHash(txHash);
    }, [txHash]);

    // --- Fetch Token Name & Symbol ---
    const { data: tokenInfo, refetch: refetchTokenInfo } = useReadContracts({
        contracts: [
            {
                address: tokenContract,
                abi: erc20Abi,
                functionName: 'name',
                chainId: currentChainId,
            },
            {
                address: tokenContract,
                abi: erc20Abi,
                functionName: 'symbol',
                chainId: currentChainId,
            },
        ],
        query: {
            enabled: isAddress(tokenContract) && currentChainId !== undefined, // Only run if tokenContract is a valid address and chainId is set
            staleTime: Infinity, // Cache forever until manually refetched
        }
    });

    // Update fetched name/symbol state when tokenInfo changes
    useEffect(() => {
        if (tokenInfo && tokenInfo.length === 2) {
            setFetchedTokenName(tokenInfo[0]?.result || '');
            setFetchedTokenSymbol(tokenInfo[1]?.result || '');
        } else {
            setFetchedTokenName('');
            setFetchedTokenSymbol('');
        }
    }, [tokenInfo]);

    // Refetch token info if tokenContract or chainId changes
    useEffect(() => {
        if (isAddress(tokenContract) && currentChainId !== undefined) {
            refetchTokenInfo();
        }
    }, [tokenContract, currentChainId, refetchTokenInfo]);


    // --- Effects ---

    // Calculate Fee and Amount per Wallet (Form)
    useEffect(() => {
        // ... calculation logic remains the same ...
        if (bnbDeposit && walletCount > 0) {
            const deposit = parseFloat(bnbDeposit);
            if (!isNaN(deposit) && deposit > 0) {
                const fee = deposit * 0.10;
                const remaining = deposit - fee;
                setCalculatedFee(fee);
                setAmountPerWallet(remaining / walletCount);
            } else {
                setCalculatedFee(0);
                setAmountPerWallet(0);
            }
        } else {
            setCalculatedFee(0);
            setAmountPerWallet(0);
        }
    }, [bnbDeposit, walletCount]);

    // Fetch Bot Details (Modified for Auto-Detection and Form Check)
    const fetchBotDetails = useCallback(async (specificToken = null, calledFromForm = false) => {
        if (!address || !currentChainId) {
            setViewMode('form');
            setDisplayStatus('Not Configured');
            setBotDetails(null);
            setTokenContract('');
            return;
        }

        // Only show full loading screen on initial load/retry, not for form input checks
        if (!calledFromForm) {
            setViewMode('loading');
            setDisplayStatus('Loading Bot Status...');
        } else {
            // Indicate verification is happening in the form helper text
            // We won't change the main displayStatus here for form checks
        }

        let endpoint = '';
        let isCheckingSpecificToken = false;

        if (specificToken && isAddress(specificToken)) {
            endpoint = `${apiUrl}/api/bot-details/${address}/${currentChainId}/${specificToken}`;
            isCheckingSpecificToken = true; // Keep track if we are checking a specific token
        } else {
            // General check for any active/pending bot
            endpoint = `${apiUrl}/api/user-active-bot/${address}/${currentChainId}`;
        }

        try {
            const response = await fetch(endpoint);

            if (!response.ok) {
                if (response.status === 404) {
                    // --- 404 Not Found Handling ---
                    if (calledFromForm && isCheckingSpecificToken) {
                        // If checking a specific token in the form and it's NOT found: GOOD.
                        // Stay in form mode. Clear any old bot details shown.
                        setBotDetails(null);
                        // Don't clear tokenContract input.
                        // Reset display status relevant to the form *only if* it wasn't already 'Not Configured'
                        // This prevents overwriting a potential "Loading..." status from initial load too quickly.
                        // The helper text below the input will provide more specific feedback.
                        if (displayStatus !== 'Not Configured') {
                             setDisplayStatus('Not Configured'); // Set main status if needed
                        }
                        // Clear fetched token info as the check concluded (no existing bot found)
                        // but keep the input value
                        setFetchedTokenName('');
                        setFetchedTokenSymbol('');
                        // viewMode remains 'form'
                    } else if (!calledFromForm) {
                        // If initial load/connect and NO active/pending bot found: Go to form.
                        setViewMode('form');
                        setDisplayStatus('Not Configured');
                        setBotDetails(null);
                        setTokenContract(''); // Clear any previous contract
                    }
                    // If calledFromForm but not checking specific token (shouldn't happen), do nothing specific.
                } else {
                    // Other HTTP errors
                    throw new Error(`Failed to fetch status (${response.status})`);
                }
            } else {
                // --- Bot Found (200 OK) ---
                const data = await response.json();

                if (calledFromForm && isCheckingSpecificToken) {
                    // If checking a specific token in the form and it IS found:
                    setBotDetails(data); // Update details
                    setTokenContract(data.tokenContract); // Ensure contract state matches found bot
                    // Update fetched name/symbol based on backend response if available
                    setFetchedTokenName(data.tokenName || '');
                    setFetchedTokenSymbol(data.tokenSymbol || '');
                    // Update main display status
                    setDisplayStatus(formatStatus(data.status, data.tokenSymbol || ''));

                    if (data.status === 'stopped') {
                        // If the found bot is 'stopped', allow configuring a new one. Stay in form.
                        toast.info(`A previous bot for ${data.tokenSymbol || data.tokenContract} exists but is stopped. You can configure a new one.`);
                        // viewMode remains 'form'
                    } else {
                        // If found bot is active, pending, stopping, error: Prevent new config for THIS token. Redirect to dashboard.
                        toast.error(`A bot for ${data.tokenSymbol || data.tokenContract} already exists (Status: ${formatStatus(data.status)}). Manage it from the dashboard.`);
                        setViewMode('dashboard'); // Redirect to show the existing bot
                    }
                } else if (!calledFromForm) {
                    // If initial load/connect and an active/pending/stopping/error bot IS found:
                    setBotDetails(data);
                    setTokenContract(data.tokenContract); // Set contract for token info fetching
                    // Update fetched name/symbol based on backend response if available
                    setFetchedTokenName(data.tokenName || '');
                    setFetchedTokenSymbol(data.tokenSymbol || '');
                    setDisplayStatus(formatStatus(data.status, data.tokenSymbol || ''));
                    // Go to dashboard ONLY if status requires management or viewing
                    if (['pending_deposit', 'pending_distribution', 'active', 'stopping', 'error'].includes(data.status)) {
                        setViewMode('dashboard');
                    } else {
                        // If backend somehow returned a 'stopped' bot via user-active-bot (shouldn't happen), go to form.
                        setViewMode('form');
                        setBotDetails(null);
                        setTokenContract('');
                    }
                }
                // If calledFromForm but not checking specific token (shouldn't happen), do nothing specific.
            }
        } catch (error) {
            console.error("Error fetching bot details:", error);
            // Show full error screen only on initial load failure
            if (!calledFromForm) {
                setViewMode('error');
                setDisplayStatus('Error Loading Status');
            } else {
                // Show toast for form check errors
                toast.error(`Error checking token: ${error.message}`);
                // Clear fetched token info in form on error
                setFetchedTokenName('');
                setFetchedTokenSymbol('');
                 // Optionally reset main status if the check failed
                 // setDisplayStatus('Error Checking Token');
            }
            setBotDetails(null);
            // Don't clear tokenContract if called from form, user might still be typing
            if (!calledFromForm) {
                setTokenContract('');
            }
        }
        // Make sure viewMode is included in dependencies if logic depends on it
    }, [address, currentChainId]); // Removed fetchedTokenSymbol and viewMode as direct dependencies, handled internally or via other effects

    // Initial fetch on load or when user/chain changes
    useEffect(() => {
        if (isConnected && address && currentChainId) {
            // Pass false for initial load check
            fetchBotDetails(null, false);
        } else {
            setViewMode('form');
            setDisplayStatus('Not Configured');
            setBotDetails(null);
            setTokenContract('');
        }
        // fetchBotDetails is now a dependency
    }, [isConnected, address, currentChainId, fetchBotDetails]);

    // Effect to check specific token when input changes
    useEffect(() => {
        // Clear previous fetched info immediately when typing starts
        setFetchedTokenName('');
        setFetchedTokenSymbol('');

        const handler = setTimeout(() => {
            // Only check if in form mode and token is a valid address
            if (viewMode === 'form' && isAddress(tokenContract)) {
                // Pass true to indicate this call is from the form input check
                fetchBotDetails(tokenContract, true);
            }
        }, 500); // Check 500ms after typing stops

        return () => {
            clearTimeout(handler);
        };
        // Pass fetchBotDetails as dependency
    }, [tokenContract, viewMode, fetchBotDetails]); // viewMode is needed here

    // Effect to trigger fund distribution after deposit confirmation
    useEffect(() => {
        // Check if the confirmation is for the current botId we expect
        // Added more specific check for submissionStatus to avoid re-triggering
        if (isConfirmed && hash && botDetails?.botId && (submissionStatus === 'Confirming Deposit Tx...')) {
            console.log("Deposit transaction confirmed:", hash);
            toast.success("Deposit confirmed! Initiating fund distribution...");
            setSubmissionStatus('Triggering Distribution...'); // Update status for UI feedback

            const triggerDistribution = async (currentBotId) => { // Pass botId explicitly
                console.log(`[triggerDistribution] Called for botId: ${currentBotId}`); // Log entry and botId
                try {
                    console.log(`[triggerDistribution] Sending POST to ${apiUrl}/api/internal/trigger-fund-distribution`);
                    const response = await fetch(`${apiUrl}/api/internal/trigger-fund-distribution`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ botId: currentBotId }), // Use passed botId
                    });

                    // Log raw response status and text
                    console.log(`[triggerDistribution] Response Status: ${response.status}`);
                    const responseText = await response.text(); // Read response text first
                    console.log(`[triggerDistribution] Response Text: ${responseText}`);

                    if (!response.ok) {
                        let errorData = { error: `Request failed with status ${response.status}` };
                        try {
                            errorData = JSON.parse(responseText); // Try to parse as JSON
                        } catch (parseError) {
                            console.warn("[triggerDistribution] Response body is not valid JSON.");
                        }
                        throw new Error(errorData.error || `Failed to trigger distribution (Status: ${response.status})`);
                    }

                    const resultData = JSON.parse(responseText); // Parse JSON now that we know it's likely okay
                    console.log("[triggerDistribution] Fund distribution triggered successfully via API.", resultData);
                    setDisplayStatus('Distributing Funds...'); // Optimistic UI update
                    // Fetch details sooner to get updated status, maybe after 5-10 seconds
                    setTimeout(() => fetchBotDetails(null, false), 10000); // Pass false

                } catch (error) {
                    console.error("[triggerDistribution] Error:", error);
                    toast.error(`Distribution trigger failed: ${error.message}`);
                    setDisplayStatus('Error: Distribution Failed');
                    // Optionally update bot status to error via API call if backend doesn't handle it
                    // await fetch(`${apiUrl}/api/update-bot-status`, { method: 'POST', ... body: { botId: currentBotId, status: 'error', message: ... } });
                } finally {
                    console.log("[triggerDistribution] Finalizing.");
                    setSubmissionStatus(''); // Clear submission status
                    setIsSubmitting(false); // Re-enable buttons
                    // Do NOT clear the hash here, keep it for display
                }
            };
            // Pass botDetails.botId explicitly to the async function
            triggerDistribution(botDetails.botId);
        } else if (isConfirming && hash && (submissionStatus === 'Waiting for Deposit Tx...' || submissionStatus === 'Waiting for Manual Deposit Tx...')) {
             // Only update status if it's still in the waiting phase
             setSubmissionStatus('Confirming Deposit Tx...'); // Update status while confirming
        }
        // Dependencies: Ensure all variables used inside are listed, especially botDetails.botId
    }, [isConfirmed, isConfirming, hash, botDetails, submissionStatus, fetchBotDetails]); // Added botDetails?.botId to dependencies if needed, but botDetails covers it


    // --- Handlers ---

    // Save Configuration & Initiate Deposit (Include Fetched Name/Symbol)
    const handleSaveAndDeposit = async () => {
        // ... (validation remains the same) ...
        if (!address) {
            toast.error("Please connect your wallet.");
            return;
        }
        if (!tokenContract || !isAddress(tokenContract) || !bnbDeposit || walletCount <= 0 || buyInterval <= 0 || parseFloat(bnbDeposit) <= 0 || parseFloat(buyAmountMin) < 0 || parseFloat(buyAmountMax) < parseFloat(buyAmountMin) || sellTriggerPercent < 0 || sellPortion < 0 || sellPortion > 100 || stopLossPercent < 0) {
            toast.error("Please fill all required fields correctly and ensure values are valid.");
            return;
        }

        setIsSubmitting(true);
        setSubmissionStatus('Saving Configuration...');

        const configuration = {
            userId: address,
            chainId: currentChainId,
            tokenContract,
            buyAmountMin: parseFloat(buyAmountMin),
            buyAmountMax: parseFloat(buyAmountMax),
            walletCount,
            initialBnbDeposit: parseFloat(bnbDeposit),
            buyIntervalSeconds: buyInterval,
            strategy: { sellTriggerPercent, sellPortion, stopLossPercent },
            feeAddress: volumeBotFeeAddress, // Assuming this is correct fee address
            // Include fetched token details
            fetchedTokenName: fetchedTokenName,
            fetchedTokenSymbol: fetchedTokenSymbol,
        };

        try {
            // 1. Save Configuration
            const configResponse = await fetch(`${apiUrl}/api/configure-volume-bot`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(configuration),
            });

            if (!configResponse.ok) {
                const errorData = await configResponse.json();
                throw new Error(errorData.error || "Failed to save configuration");
            }

            const configResult = await configResponse.json();
            console.log("Configuration saved:", configResult);
            toast.success("Configuration saved. Preparing deposit...");

            // Store botId AND feeAddress/depositAmount needed for deposit
            // Also update botDetails with potentially updated name/symbol from backend response if needed
            setBotDetails(prev => ({
                ...prev,
                botId: configResult.botId,
                feeAddress: configResult.feeAddress, // Store fee address
                depositAmount: configResult.depositAmount, // Store deposit amount
                // Update name/symbol from backend if they were missing before
                tokenName: prev?.tokenName === 'N/A' ? fetchedTokenName : prev?.tokenName,
                tokenSymbol: prev?.tokenSymbol === 'N/A' ? fetchedTokenSymbol : prev?.tokenSymbol,
            }));

            // 2. Initiate Deposit Transaction
            const depositAmountWei = parseEther(configResult.depositAmount.toString());
            const feeAddress = configResult.feeAddress;

            setSubmissionStatus('Waiting for Deposit Tx...'); // Specific status for auto-deposit
            sendTransaction({
                to: feeAddress,
                value: depositAmountWei,
            });
            // useEffect handles confirmation and distribution

        } catch (error) {
            console.error("Error during Save & Deposit:", error);
            toast.error(`Operation failed: ${error.message}`);
            setSubmissionStatus('');
            setIsSubmitting(false);
            // Fetch status again to reflect potential backend state
            fetchBotDetails(null, false); // Pass false
        }
        // No finally block for setIsSubmitting here, handled by useEffect or error catch
    };

    // Initiate Deposit Manually (New Handler for Dashboard Button)
    const handleManualDeposit = () => {
        if (!botDetails || !botDetails.feeAddress || !botDetails.depositAmount) {
            toast.error("Bot details not loaded correctly. Cannot initiate deposit.");
            return;
        }
        if (!address) {
            toast.error("Please connect your wallet.");
            return;
        }

        setIsSubmitting(true);
        setSubmissionStatus('Waiting for Manual Deposit Tx...'); // Specific status for manual deposit

        try {
            const depositAmountWei = parseEther(botDetails.depositAmount.toString());
            const feeAddress = botDetails.feeAddress;

            sendTransaction({
                to: feeAddress,
                value: depositAmountWei,
            });
            // useEffect handles confirmation and distribution

        } catch (error) {
            console.error("Error initiating manual deposit:", error);
            toast.error(`Deposit failed: ${error.message}`);
            setSubmissionStatus('');
            setIsSubmitting(false);
        }
    };

    // Stop Bot
    const handleStopBot = async () => {
        // Allow stopping even in pending_deposit state
        if (!address || !botDetails?.tokenContract) {
            toast.error("Cannot stop bot: No configuration found or wallet disconnected.");
            return;
        }

        setIsSubmitting(true);
        setSubmissionStatus('Stopping Bot...');
        // Optimistic UI update might still use botDetails.tokenSymbol if available
        setDisplayStatus(formatStatus('stopping', fetchedTokenSymbol || botDetails?.tokenSymbol));

        try {
            const response = await fetch(`${apiUrl}/api/stop-volume-bot`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                // Ensure chainId is included
                body: JSON.stringify({ userId: address, tokenContract: botDetails.tokenContract, chainId: currentChainId }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to stop bot");
            }

            toast.success("Bot stop request sent successfully.");
            // Fetch details again using initial load logic to reflect change
            await fetchBotDetails(null, false); // Pass false

        } catch (error) {
            console.error("Error stopping bot:", error);
            toast.error(`Failed to stop bot: ${error.message}`);
            // Fetch details again to show the actual current status
            await fetchBotDetails(null, false); // Pass false
        } finally {
            setSubmissionStatus('');
            setIsSubmitting(false);
        }
    };

    // Handle "Create New Bot" button click
    const handleCreateNew = () => {
        setViewMode('form');
        setBotDetails(null); // Clear details of the old bot
        setTokenContract(''); // Clear the contract input
        setFetchedTokenName(''); // Clear fetched info
        setFetchedTokenSymbol(''); // Clear fetched info
        setBnbDeposit(''); // Clear deposit amount
        // Reset other form fields to defaults if desired
        setBuyAmountMin('0.012');
        setBuyAmountMax('0.022');
        setWalletCount(4);
        setBuyInterval(60);
        setSellTriggerPercent(30);
        setSellPortion(50);
        setStopLossPercent(10);
        setDisplayStatus('Not Configured'); // Reset status message
        setHash(null); // Clear last transaction hash display
    };

    // Delete Bot
    const handleDeleteBot = async () => {
        if (!address || !botDetails?.tokenContract || !botDetails?.chainId) {
            toast.error("Cannot delete bot: Configuration details missing or wallet disconnected.");
            return;
        }

        // Allow deletion for 'stopped' or 'error' states
        if (!['stopped', 'error', 'stopping'].includes(botDetails.status)) {
            toast.error(`Cannot delete bot in '${botDetails.status}' state. Stop the bot first if it's active.`);
            return;
        }

        const confirmation = window.confirm(`Are you sure you want to permanently delete the bot configuration for ${fetchedTokenSymbol || botDetails.tokenSymbol || botDetails.tokenContract}? This action cannot be undone.`);

        if (!confirmation) {
            return;
        }

        setIsSubmitting(true);
        setSubmissionStatus('Deleting Bot...');

        try {
            const response = await fetch(`${apiUrl}/api/delete-volume-bot`, {
                method: 'POST', // Using POST for simplicity, could be DELETE
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: address,
                    tokenContract: botDetails.tokenContract,
                    chainId: botDetails.chainId // Ensure chainId is sent
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to delete bot");
            }

            toast.success("Bot configuration deleted successfully.");
            // Reset UI to the initial form state
            handleCreateNew(); // Re-use the logic to reset the form

        } catch (error) {
            console.error("Error deleting bot:", error);
            toast.error(`Failed to delete bot: ${error.message}`);
            // Fetch details again in case of error to show the actual current state
            await fetchBotDetails(null, false);
        } finally {
            setSubmissionStatus('');
            setIsSubmitting(false);
        }
    };


    // --- Render Logic ---

    const renderLoading = () => (
        <div className="p-10 text-center">
            <ClipLoader color={'#45c0be'} loading={true} size={50} />
            <p className="mt-4 text-white">{displayStatus}</p>
        </div>
    );

    const renderError = () => (
         <div className="p-10 text-center text-red-500">
             <p>{displayStatus}</p>
             {/* Pass false to retry the initial load fetch */}
             <button onClick={() => fetchBotDetails(null, false)} className="px-4 py-2 mt-4 text-white bg-gray-600 rounded">Retry</button>
         </div>
     );

    const renderForm = () => (
        <>
            <div className="pb-4 mb-6 text-center border-b border-gray-600">
                <h1 className="text-[#fff] text-[32px] font-bold">Configure Volume Bot</h1>
                <p className="text-[#d3d3d3] text-[14px] mt-1">
                    Automate trading volume for your token. Status: <span className='font-bold'>{displayStatus}</span>
                </p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {/* Column 1: Bot Settings */}
                <div>
                    <h2 className="text-[#fff] w-full text-[20px] font-bold mb-4">Bot Settings</h2>
                    {/* Token Contract */}
                    <section className="flex flex-col w-full mb-4">
                        <p className="text-[12px] text-[#7db4e5] mb-2 order-1">Token Contract Address<span style={{ color: 'red' }}>*</span></p>
                        <div className="order-2 bg-[#050f1a] px-[16px] py-[14px] rounded-[10px] w-full">
                            <Input placeholder="Enter token contract address" type="text" changeValue={setTokenContract} value={tokenContract} disabled={isSubmitting} />
                        </div>
                        {/* Display Fetched Token Info / Verification Status */}
                        {isAddress(tokenContract) && fetchedTokenName && fetchedTokenSymbol && (
                            <p className='mt-1 text-xs text-green-400'>
                                Token Found: {fetchedTokenName} ({fetchedTokenSymbol})
                            </p>
                        )}
                         {isAddress(tokenContract) && !fetchedTokenName && !fetchedTokenSymbol && !botDetails && displayStatus === 'Not Configured' && (
                             // This state means the check completed (404), no existing bot found, and no token info fetched yet (or cleared)
                             <p className='mt-1 text-xs text-green-400'>
                                 Token address available.
                             </p>
                         )}
                         {isAddress(tokenContract) && !fetchedTokenName && !fetchedTokenSymbol && !(displayStatus === 'Not Configured' && !botDetails) && (
                             // This state means it's likely still verifying or encountered an issue during verification
                             <p className='mt-1 text-xs text-yellow-400'>
                                 Verifying token...
                             </p>
                         )}
                         {/* Add a case for when an existing (non-stopped) bot IS found during check */}
                         {isAddress(tokenContract) && botDetails && botDetails.status !== 'stopped' && viewMode === 'form' && (
                             <p className='mt-1 text-xs text-red-500'>
                                 A bot already exists for this token (Status: {formatStatus(botDetails.status)}). Go to Dashboard.
                             </p>
                         )}
                         {/* Add a case for when a stopped bot IS found during check */}
                         {isAddress(tokenContract) && botDetails && botDetails.status === 'stopped' && viewMode === 'form' && (
                             <p className='mt-1 text-xs text-yellow-500'>
                                 A stopped bot exists for {botDetails.tokenSymbol || botDetails.tokenContract}. You can create a new one.
                             </p>
                         )}
                    </section>
                    {/* Wallet Count */}
                    <section className="flex flex-col w-full mb-4">
                        <p className="text-[12px] text-[#7db4e5] mb-2 order-1">Number of Wallets<span style={{ color: 'red' }}>*</span></p>
                        <div className="order-2 bg-[#050f1a] px-[16px] py-[14px] rounded-[10px] w-full">
                            <Input placeholder="e.g., 4" type="number" changeValue={(val) => setWalletCount(parseInt(val) || 0)} value={walletCount.toString()} min="1" disabled={isSubmitting} />
                        </div>
                    </section>
                    {/* Deposit Amount */}
                    <section className="flex flex-col w-full mb-4">
                        <p className="text-[12px] text-[#7db4e5] mb-2 order-1">Total {coinNames[currentChainId]} to Deposit<span style={{ color: 'red' }}>*</span></p>
                        <div className="order-2 bg-[#050f1a] px-[16px] py-[14px] rounded-[10px] w-full">
                            <Input placeholder={`e.g., 1 ${coinNames[currentChainId]}`} type="number" changeValue={setBnbDeposit} value={bnbDeposit} min="0" disabled={isSubmitting} />
                        </div>
                        {bnbDeposit && (
                            <div className='text-xs text-[#d3d3d3] mt-2'>
                                <p>Service Fee (10%): {calculatedFee.toFixed(4)} {coinNames[currentChainId]}</p>
                                <p>Amount per Bot Wallet ({walletCount}): {amountPerWallet.toFixed(4)} {coinNames[currentChainId]}</p>
                                <p className='mt-1 text-yellow-400'>Note: The total deposit will be sent automatically after saving.</p>
                            </div>
                        )}
                    </section>
                    {/* Buy Interval */}
                    <section className="flex flex-col w-full mb-4">
                        <p className="text-[12px] text-[#7db4e5] mb-2 order-1">Buy Interval (seconds)<span style={{ color: 'red' }}>*</span></p>
                        <div className="order-2 bg-[#050f1a] px-[16px] py-[14px] rounded-[10px] w-full">
                            <Input placeholder="e.g., 60" type="number" changeValue={(val) => setBuyInterval(parseInt(val) || 0)} value={buyInterval.toString()} min="1" disabled={isSubmitting} />
                        </div>
                    </section>
                </div>

                {/* Column 2: Trading Parameters */}
                <div>
                    <h2 className="text-[#fff] w-full text-[20px] font-bold mb-4">Trading Parameters</h2>
                    {/* Buy Amount Range */}
                    <section className="flex flex-col w-full mb-4">
                        <p className="text-[12px] text-[#7db4e5] mb-2 order-1">Buy Amount Range ({coinNames[currentChainId]})<span style={{ color: 'red' }}>*</span></p>
                        <div className="flex gap-2">
                            <div className="order-2 bg-[#050f1a] px-[16px] py-[14px] rounded-[10px] w-1/2">
                                <Input placeholder="Min" type="number" changeValue={setBuyAmountMin} value={buyAmountMin} min="0" disabled={isSubmitting} />
                            </div>
                            <div className="order-2 bg-[#050f1a] px-[16px] py-[14px] rounded-[10px] w-1/2">
                                <Input placeholder="Max" type="number" changeValue={setBuyAmountMax} value={buyAmountMax} min="0" disabled={isSubmitting} />
                            </div>
                        </div>
                        <p className='text-xs text-[#d3d3d3] mt-1'>Bot will buy random amounts between Min and Max.</p>
                    </section>
                    {/* Selling Strategy */}
                    <h3 className="text-[#fff] w-full text-[16px] font-bold mt-6 mb-2">Intelligent Selling Strategy</h3>
                    <section className="flex flex-col w-full mb-4">
                        <p className="text-[12px] text-[#7db4e5] mb-2 order-1">Sell Trigger (% Price Increase)</p>
                        <div className="order-2 bg-[#050f1a] px-[16px] py-[14px] rounded-[10px] w-full">
                            <Input placeholder="e.g., 30" type="number" changeValue={(val) => setSellTriggerPercent(parseInt(val) || 0)} value={sellTriggerPercent.toString()} min="0" disabled={isSubmitting} />
                        </div>
                    </section>
                    <section className="flex flex-col w-full mb-4">
                        <p className="text-[12px] text-[#7db4e5] mb-2 order-1">Portion to Sell (%)</p>
                        <div className="order-2 bg-[#050f1a] px-[16px] py-[14px] rounded-[10px] w-full">
                            <Input placeholder="e.g., 50" type="number" changeValue={(val) => setSellPortion(parseInt(val) || 0)} value={sellPortion.toString()} min="0" max="100" disabled={isSubmitting} />
                        </div>
                    </section>
                    <section className="flex flex-col w-full mb-4">
                        <p className="text-[12px] text-[#7db4e5] mb-2 order-1">Stop-Loss (% Price Decrease after Trigger)</p>
                        <div className="order-2 bg-[#050f1a] px-[16px] py-[14px] rounded-[10px] w-full">
                            <Input placeholder="e.g., 10" type="number" changeValue={(val) => setStopLossPercent(parseInt(val) || 0)} value={stopLossPercent.toString()} min="0" disabled={isSubmitting} />
                        </div>
                        <p className='text-xs text-[#d3d3d3] mt-1'>Sells remaining portion if price drops by this % after initial sell trigger.</p>
                    </section>
                </div>
            </div>

            {/* Action Button */}
            <div className="flex justify-center gap-4 mt-8">
                <button
                    disabled={isSubmitting || !address || !tokenContract || !bnbDeposit}
                    onClick={handleSaveAndDeposit}
                    className="px-6 py-3 bg-gradient-to-r from-[#1c5290] to-[#45c0be] text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 min-w-[200px]"
                >
                    {isSubmitting ? <ClipLoader color={'#fff'} loading={isSubmitting} size={20} /> : null}
                    <span>{submissionStatus || 'Save & Deposit'}</span>
                </button>
            </div>
            {!address && <div className='mt-4 text-center text-red-500'>Please connect your wallet to configure the bot.</div>}
            {hash && <div className='mt-4 text-xs text-center text-gray-400'>Last Tx Hash: {hash}</div>}
            {submissionStatus && <div className='mt-2 text-sm font-bold text-center text-yellow-400'>{submissionStatus}</div>}
            <div className='mt-4 text-sm text-center text-yellow-500'>Note: Saving configuration will initiate the deposit transaction from your wallet.</div>
        </>
    );

    const renderDashboard = () => {
        const explorerUrl = web3Clients[currentChainId]?.blockExplorers?.default?.url;
        const isPendingDeposit = botDetails?.status === 'pending_deposit';
        const canDelete = ['stopped', 'error', 'stopping'].includes(botDetails?.status); // Define when delete is allowed

        // Use fetched name/symbol as primary source, fallback to botDetails, then 'N/A'
        const displayName = fetchedTokenName || botDetails?.tokenName || 'N/A';
        const displaySymbol = fetchedTokenSymbol || botDetails?.tokenSymbol || 'N/A';

        return (
            <>
                <div className="pb-4 mb-6 text-center border-b border-gray-600">
                    <h1 className="text-[#fff] text-[32px] font-bold">Volume Bot Dashboard</h1>
                     <p className="text-[#d3d3d3] text-[14px] mt-1">
                         {/* Update status display to use displaySymbol */}
                         Status: <span className={`font-bold ${botDetails?.status === 'error' ? 'text-red-500' : (isPendingDeposit ? 'text-yellow-400' : 'text-green-400')}`}>{formatStatus(botDetails?.status, displaySymbol)}</span>
                         {botDetails?.status === 'error' && <span className="block text-xs text-red-400">({botDetails.statusMessage || 'Unknown error'})</span>}
                     </p>
                </div>


                {botDetails && (
                    <div className="grid grid-cols-1 gap-6 text-white md:grid-cols-2">
                        {/* Bot Info */}
                        <div>
                            <h2 className="text-[20px] font-bold mb-3">Bot Information</h2>
                            <div className="space-y-2 text-sm bg-[#050f1a] p-4 rounded-lg">
                                {/* Use displayName and displaySymbol */}
                                <p><strong>Token:</strong> {displayName} ({displaySymbol})</p>
                                <p>
                                    <strong>Contract:</strong>{' '}
                                    {explorerUrl && botDetails.tokenContract ? (
                                        <a href={`${explorerUrl}/address/${botDetails.tokenContract}`} target="_blank" rel="noopener noreferrer" className="text-[#7db4e5] hover:underline break-all">
                                            {botDetails.tokenContract}
                                        </a>
                                    ) : (
                                        <span className="break-all">{botDetails.tokenContract || 'N/A'}</span>
                                    )}
                                </p>
                                <p><strong>Deposit Required:</strong> {botDetails.depositAmount} {coinNames[currentChainId]}</p>
                                <p><strong>Fee Address:</strong> <span className="text-xs break-all">{botDetails.feeAddress}</span></p>
                                <p><strong>Wallets:</strong> {botDetails.walletCount}</p>
                                <p><strong>Market Cap:</strong> {botDetails.marketCap || 'N/A'} </p>
                                <p><strong>Generated Volume:</strong> {botDetails.generatedVolume?.toFixed(4) || '0.0000'} {coinNames[currentChainId]}</p>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col items-center justify-center gap-4">
                             {/* Deposit Button - Show only if pending deposit */}
                             {isPendingDeposit && (
                                 <button
                                     disabled={isSubmitting || !address}
                                     onClick={handleManualDeposit}
                                     className="w-full max-w-xs px-6 py-3 bg-gradient-to-r from-[#1c9052] to-[#45c07b] text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                                 >
                                     {isSubmitting && (submissionStatus === 'Waiting for Manual Deposit Tx...' || submissionStatus === 'Confirming Deposit Tx...') ? <ClipLoader color={'#fff'} loading={true} size={20} /> : null}
                                     <span>{submissionStatus === 'Waiting for Manual Deposit Tx...' || submissionStatus === 'Confirming Deposit Tx...' ? submissionStatus : `Deposit ${botDetails.depositAmount} ${coinNames[currentChainId]} Now`}</span>
                                 </button>
                             )}

                             {/* Stop Button - Allow stopping in multiple states */}
                             <button
                                 disabled={isSubmitting || !['active', 'pending_deposit', 'error', 'pending_distribution'].includes(botDetails.status)}
                                 onClick={handleStopBot}
                                 className="w-full max-w-xs px-6 py-3 bg-gradient-to-r from-[#c04545] to-[#901c1c] text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                             >
                                 {isSubmitting && submissionStatus === 'Stopping Bot...' ? <ClipLoader color={'#fff'} loading={true} size={20} /> : null}
                                 <span>{submissionStatus === 'Stopping Bot...' ? `Stopping (${displaySymbol})...` : 'Stop Bot'}</span>
                             </button>

                             {/* Delete Button - Show only if stopped, error, or stopping */}
                             {canDelete && (
                                 <button
                                     disabled={isSubmitting}
                                     onClick={handleDeleteBot}
                                     className="flex items-center justify-center w-full max-w-xs gap-2 px-6 py-3 text-white bg-red-800 rounded-lg hover:bg-red-700 disabled:opacity-50"
                                 >
                                     {isSubmitting && submissionStatus === 'Deleting Bot...' ? <ClipLoader color={'#fff'} loading={true} size={20} /> : null}
                                     <span>{submissionStatus === 'Deleting Bot...' ? 'Deleting...' : 'Delete Bot'}</span>
                                 </button>
                             )}


                             {/* Configure New Button */}
                             <button
                                 disabled={isSubmitting}
                                 onClick={handleCreateNew}
                                 className="w-full max-w-xs px-6 py-3 text-white bg-gray-600 rounded-lg hover:bg-gray-700 disabled:opacity-50"
                             >
                                 Configure New Bot
                             </button>
                        </div>
                    </div>
                )}
                 {hash && <div className='mt-4 text-xs text-center text-gray-400'>Last Tx Hash: {hash}</div>}
                 {submissionStatus && <div className='mt-2 text-sm font-bold text-center text-yellow-400'>{submissionStatus}</div>}
            </>
        );
    };


    return (
        <div>
            <div className="GlobalContainer">
                <div style={{ zIndex: 1 }}>
                    <div className="max-w-7xl m-auto pt-36 pb-24 sm:py-10">
                        <section className="lg:mx-auto pt-8 lg:py-[30px] w-full min-w-0 bg-[#6d79ad33] rounded-[10px] lg:px-8 px-4 py-5 backdrop-blur-[8px] border-2 border-[#ffffff66]">
                            {viewMode === 'loading' && renderLoading()}
                            {viewMode === 'error' && renderError()}
                            {viewMode === 'form' && renderForm()}
                            {viewMode === 'dashboard' && renderDashboard()}
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VolumeBot;
